import React, { useEffect, useState, useCallback } from "react";
import { useTheme, alpha, darken } from "@mui/material/styles";
import {
  Box,
  Typography,
  Button,
  
  Stack,
  
  TextField,
  
  MenuItem,
  
  
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  LinearProgress,
  Pagination,
  
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  
  Autocomplete,
  
  
  
  
  
  
  Menu,
  List,
  ListItemButton,
  ListItemText,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  fetchClasses,
  createClass,
  
  deleteClass,
  fetchUsers,
} from "../api/backend";
import ConfirmDialog from "../components/ConfirmDialog";
import { useSaveClassToBank } from "../components/SaveClassToBank";
import { fetchClassTemplates, createClassTemplateFromClass, syncClassTemplateFromClass, deleteClassTemplate, createSession, fetchTenantUsersByRole, fetchCertificateTypes, saveClassCertificateTypes } from "../api/apiClient";
import CertificateTypesPicker from "../components/certificates/CertificateTypesPicker";
import { fetchRooms, fetchSites } from "../api/rooms";
import LocationSelect from "../components/LocationSelect";
import { VIRTUAL, isVirtual, locationText, sessionLocationFields } from "../utils/locations";
import { useActiveTenant } from "../hooks/useActiveTenant";
import NoTenantSelected from "../components/NoTenantSelected";

import FileDownloadIcon from "@mui/icons-material/FileDownload";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SchoolIcon from "@mui/icons-material/School";
import GroupIcon from "@mui/icons-material/Group";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useConfigList } from "../hooks/useConfigList";

// ── Design tokens (matches project DS) ──────────────────────────────────────
const DS = {
  ink:      "var(--color-ink)", ink2: "var(--color-ink-2)", ink3: "var(--color-ink-3)", ink4: "var(--color-ink-4)",
  line:     "var(--color-hairline-cloud)", line2: "var(--color-surface-press-light)", line3: "var(--color-surface-press-light)",
  surface:  "#FFFFFF", surface2: "var(--color-surface-2)", surface3: "var(--color-surface-press-light)",
  pageBg:   "var(--color-surface-2)",
  primary50: "var(--color-primary-10)",
  live:     "#C2410C", live50: "rgba(194,65,12,.10)",  live100: "rgba(194,65,12,.22)",
  danger:   "#B42318", danger50: "rgba(180,35,24,.08)", danger100: "rgba(180,35,24,.18)",
  blue:     "var(--color-primary)", blue50: "rgba(37,99,235,.10)", blue100: "rgba(37,99,235,.18)",
  // legacy aliases kept for CourseDialog compatibility
  primaryContainer: "#002A59",
  secondary: "#15803D",
  surfaceContainerLowest: "#FFFFFF",
  surfaceContainerLow: "var(--color-surface-2)",
  surfaceContainerHigh: "var(--color-hairline-cloud)",
  onSurface: "var(--color-ink)",
  onSurfaceVariant: "var(--color-ink-2)",
  outlineVariant: "var(--color-hairline-cloud)",
};

function useDS() {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const secondary = theme.palette.secondary.main;
  const accent = theme.palette.warning.main;
  return {
    ...DS,
    primary,
    primary700: darken(primary, 0.3),
    primaryContainer: darken(primary, 0.3),
    primary10: alpha(primary, 0.06),
    green: secondary,
    green50: alpha(secondary, 0.10),
    green100: alpha(secondary, 0.20),
    amber: accent,
    amber50: alpha(accent, 0.10),
    amber100: alpha(accent, 0.22),
    secondary,
  };
}

const COURSES_PER_PAGE = 10;

// ── Derive course status from data ───────────────────────────────────────────
function getCourseStatus(course, enrollment) {
  if (course.publishStatus === "DRAFT") return "DRAFT";
  const now = new Date();
  const courseDate = new Date(course.date);
  const endDate = course.schedule?.length
    ? new Date(course.schedule[course.schedule.length - 1].end)
    : courseDate;
  if (endDate < now) return "COMPLETED";
  if (enrollment?.enrolled >= enrollment?.capacity && enrollment?.capacity > 0)
    return "WAITLIST_ONLY";
  return "REGISTRATION_OPEN";
}

const STATUS_CFG = {
  REGISTRATION_OPEN: { label: "Registration Open", bg: "rgba(21,128,61,.10)", color: "#15803D", border: "rgba(21,128,61,.20)", pulse: true },
  COMPLETED:         { label: "Completed",         bg: "var(--color-surface-press-light)",             color: "var(--color-ink-3)", border: "var(--color-hairline-cloud)",            pulse: false },
  WAITLIST_ONLY:     { label: "Waitlist Only",      bg: "rgba(181,101,29,.10)", color: "#B5651D", border: "rgba(181,101,29,.22)", pulse: true },
  DRAFT:             { label: "Draft",              bg: "var(--color-surface-press-light)",             color: "var(--color-ink-3)", border: "var(--color-hairline-cloud)",            pulse: false },
};

function StatusPill({ status }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.REGISTRATION_OPEN;
  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center", gap: "5px",
      fontSize: "11px", fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase",
      padding: "4px 10px", borderRadius: "999px",
      bgcolor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
      whiteSpace: "nowrap",
    }}>
      <Box component="span" sx={{
        width: 5, height: 5, borderRadius: "50%", bgcolor: "currentColor", flexShrink: 0,
        ...(cfg.pulse ? {
          "@keyframes spulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.4 } },
          animation: "spulse 2s ease-in-out infinite",
        } : {}),
      }} />
      {cfg.label}
    </Box>
  );
}

function EnrollmentBar({ enrolled, capacity }) {
  const theme = useTheme();
  const pct = capacity > 0 ? Math.min((enrolled / capacity) * 100, 100) : 0;
  const barColor = pct >= 100 ? "#B42318" : pct >= 75 ? "#B5651D" : pct >= 40 ? theme.palette.primary.main : pct > 0 ? "#15803D" : "var(--color-hairline-cloud)";
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 120 }}>
      <Box sx={{ flex: 1, height: 6, bgcolor: "var(--color-surface-press-light)", borderRadius: "999px", overflow: "hidden" }}>
        <Box sx={{ height: "100%", width: `${pct}%`, bgcolor: barColor, borderRadius: "999px", transition: "width .3s" }} />
      </Box>
      <Typography sx={{ fontSize: "12.5px", fontWeight: 700, color: "var(--color-ink)", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
        {enrolled}/{capacity}
      </Typography>
    </Box>
  );
}

function getCatConfig(category = "", tPrimary = "var(--color-primary)") {
  const c = category.toLowerCase();
  if (c.includes("fire") || c.includes("wildland"))
    return { bg: "rgba(194,65,12,.10)", color: "#C2410C" };
  if (c.includes("ems") || c.includes("aha") || c.includes("cpr") || c.includes("medic") || c.includes("bls"))
    return { bg: "rgba(37,99,235,.10)", color: "var(--color-primary)" };
  if (c.includes("hazmat") || c.includes("haz"))
    return { bg: "rgba(181,101,29,.10)", color: "#B5651D" };
  if (c.includes("officer") || c.includes("police") || c.includes("tactical") || c.includes("law"))
    return { bg: alpha(tPrimary, 0.06), color: tPrimary };
  if (c.includes("rescue") || c.includes("search") || c.includes("lifeguard") || c.includes("aqua"))
    return { bg: "rgba(21,128,61,.10)", color: "#15803D" };
  return { bg: alpha(tPrimary, 0.06), color: tPrimary };
}

function getInstrColor(name = "") {
  const COLORS = ["var(--color-primary)", "var(--color-primary)", "#15803D", "#B5651D", "var(--color-ink-3)", "#C2410C"];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffffffff;
  return COLORS[Math.abs(h) % COLORS.length];
}

function InstrAvatar({ name }) {
  const initials = (name || "?").split(" ").slice(0, 2).map(n => n[0]).join("").toUpperCase();
  return (
    <Box sx={{ width: 26, height: 26, borderRadius: "50%", bgcolor: getInstrColor(name), flexShrink: 0,
      display: "grid", placeItems: "center", fontSize: "10px", fontWeight: 800, color: "#fff" }}>
      {initials}
    </Box>
  );
}

function CategoryPill({ category }) {
  const theme = useTheme();
  if (!category) return <Typography sx={{ fontSize: 13, color: "var(--color-ink-3)" }}>—</Typography>;
  const { bg, color } = getCatConfig(category, theme.palette.primary.main);
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: "5px",
      fontSize: "11px", fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase",
      padding: "3px 9px", borderRadius: "999px", bgcolor: bg, color }}>
      <Box component="span" sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: "currentColor" }} />
      {category}
    </Box>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
}

// ── Create/Edit Course Dialog ─────────────────────────────────────────────────
const DEFAULT_CATEGORIES = [
  "Firefighter",
  "EMS",
  "Police",
  "Rescue",
  "Lifeguard",
  "Security",
  "ADMIN",
  "AHA",
  "TACTICAL",
  "Other",
];

function SeatStepper({ value, onChange }) {
  const DS = useDS();
  const btnSx = {
    width: 38, height: "100%", flexShrink: 0,
    bgcolor: "transparent", border: "none", p: 0, m: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer", color: DS.ink3, fontSize: "19px", fontFamily: "inherit",
    transition: "background .12s, color .12s",
    "&:hover:not(:disabled)": { bgcolor: DS.surface3, color: DS.ink },
    "&:disabled": { opacity: 0.35, cursor: "default" },
  };
  return (
    <Box sx={{
      height: 40, display: "flex", alignItems: "stretch",
      bgcolor: DS.surface2, border: `1.5px solid ${DS.line}`, borderRadius: "11px",
      overflow: "hidden", minWidth: 116,
      "&:focus-within": { borderColor: DS.primary, boxShadow: `0 0 0 3px ${alpha(DS.primary, 0.08)}` },
    }}>
      <Box component="button" type="button" sx={btnSx}
        onClick={() => onChange(Math.max(1, value - 1))} disabled={value <= 1}>−</Box>
      <Box component="input" type="number" value={value} min={1} max={999}
        onChange={(e) => { const n = parseInt(e.target.value); if (!isNaN(n) && n >= 1) onChange(n); }}
        sx={{
          flex: 1, bgcolor: "transparent", border: "none",
          borderLeft: `1px solid ${DS.line2}`, borderRight: `1px solid ${DS.line2}`,
          textAlign: "center", fontFamily: "inherit", fontSize: 15, fontWeight: 700,
          color: DS.ink, outline: "none", MozAppearance: "textfield", minWidth: 0,
          "&::-webkit-inner-spin-button,&::-webkit-outer-spin-button": { WebkitAppearance: "none" },
        }} />
      <Box component="button" type="button" sx={btnSx}
        onClick={() => onChange(Math.min(999, value + 1))}>+</Box>
    </Box>
  );
}


function PrereqCourseSelect({ tenantId, selected, onChange }) {
  const DS = useDS();
  const [courses, setCourses] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const wrapRef = React.useRef();

  React.useEffect(() => {
    if (!tenantId) return;
    fetchClasses(tenantId, "").then((list) => setCourses(list || [])).catch(() => {});
  }, [tenantId]);

  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const selectedIds = selected.map((s) => s.id);
  const filtered = courses.filter(
    (c) => !selectedIds.includes(c.id) &&
      (c.title || "").toLowerCase().includes(query.toLowerCase())
  );

  const remove = (id) => onChange(selected.filter((s) => s.id !== id));
  const add = (c) => { onChange([...selected, { id: c.id, title: c.title }]); setQuery(""); };

  return (
    <Box ref={wrapRef} sx={{ position: "relative" }}>
      <Box
        onClick={() => setOpen(true)}
        sx={{
          minHeight: 42, p: "6px 10px", cursor: "text",
          bgcolor: open ? "#fff" : DS.surface2,
          border: `1.5px solid ${open ? DS.primary : DS.line}`, borderRadius: "11px",
          display: "flex", flexWrap: "wrap", alignItems: "center", gap: "5px",
          transition: "border-color .13s, box-shadow .13s, background .13s",
          ...(open && { boxShadow: `0 0 0 3px ${alpha(DS.primary, 0.08)}` }),
        }}>
        {selected.map((s) => (
          <Box key={s.id} sx={{
            display: "inline-flex", alignItems: "center", gap: "4px",
            height: 26, px: "9px", borderRadius: "7px",
            bgcolor: DS.primary10, border: `1px solid ${DS.primary50}`,
            color: DS.primary, fontSize: 12.5, fontWeight: 600,
          }}>
            {s.title}
            <Box component="button" type="button"
              onClick={(e) => { e.stopPropagation(); remove(s.id); }}
              sx={{ display: "flex", alignItems: "center", p: 0, m: 0, bgcolor: "transparent",
                border: "none", cursor: "pointer", color: "inherit", opacity: 0.6, lineHeight: 1,
                "&:hover": { opacity: 1 } }}>
              <CloseIcon sx={{ fontSize: 10 }} />
            </Box>
          </Box>
        ))}
        <Box component="input"
          placeholder={selected.length === 0 ? "Search existing courses…" : "Add more…"}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          sx={{ flex: 1, minWidth: 120, height: 28, bgcolor: "transparent",
            border: "none", outline: "none", fontFamily: "inherit",
            fontSize: "13.5px", color: DS.ink, p: 0,
            "&::placeholder": { color: DS.ink4 } }} />
      </Box>

      {open && (
        <Box sx={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 200,
          bgcolor: "#fff", border: `1.5px solid ${DS.line}`, borderRadius: "11px",
          boxShadow: "0 8px 24px -8px rgba(20,24,40,.18)", maxHeight: 220, overflowY: "auto",
        }}>
          {filtered.length === 0 ? (
            <Typography sx={{ p: "12px 14px", fontSize: 13, color: DS.ink4 }}>
              {query ? "No matching courses" : selected.length === courses.length ? "All courses selected" : "No courses available"}
            </Typography>
          ) : filtered.map((c) => (
            <Box key={c.id} onClick={() => add(c)}
              sx={{ p: "10px 14px", cursor: "pointer", display: "flex", flexDirection: "column", gap: "2px",
                transition: "background .1s",
                "&:hover": { bgcolor: DS.primary10 },
                "&:not(:last-child)": { borderBottom: `1px solid ${DS.line2}` } }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: DS.ink }}>{c.title}</Typography>
              {c.courseNumber && (
                <Typography sx={{ fontSize: 11, fontFamily: "'JetBrains Mono',monospace", color: DS.ink4 }}>
                  {c.courseNumber}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

function CourseLivePreview({ title, courseNumber, category, seats, desc }) {
  const theme = useTheme();
  const { bg: catBg, color: catColor } = getCatConfig(category || "", theme.palette.primary.main);
  const hasContent = !!(title || courseNumber || category);
  if (!hasContent) {
    return (
      <Box sx={{
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        gap: "11px", p: "28px 12px", textAlign: "center",
        border: `1.5px dashed ${DS.line}`, borderRadius: "14px", minHeight: 140,
      }}>
        <Box sx={{ width: 40, height: 40, borderRadius: "11px", bgcolor: DS.surface3,
          display: "grid", placeItems: "center" }}>
          <SchoolIcon sx={{ fontSize: 20, color: DS.ink4 }} />
        </Box>
        <Typography sx={{ fontSize: 12, lineHeight: 1.45, color: DS.ink4 }}>
          Start filling in the form to see a live preview
        </Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ bgcolor: "#fff", border: `1.5px solid ${DS.line}`, borderRadius: "14px",
      overflow: "hidden", boxShadow: "0 1px 0 rgba(20,24,40,.04),0 1px 3px rgba(20,24,40,.06)" }}>
      <Box sx={{ p: "14px 14px 12px", display: "flex", alignItems: "flex-start", gap: "10px" }}>
        <Box sx={{ width: 36, height: 36, borderRadius: "10px", flexShrink: 0,
          display: "grid", placeItems: "center",
          bgcolor: category ? catBg : DS.surface3, color: category ? catColor : DS.ink4 }}>
          <SchoolIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: "-.01em", lineHeight: 1.3,
            color: title ? DS.ink : DS.ink4, fontStyle: title ? "normal" : "italic" }}>
            {title || "Untitled course"}
          </Typography>
          {courseNumber && (
            <Typography sx={{ fontSize: "10.5px", fontFamily: "'JetBrains Mono',monospace",
              fontWeight: 600, color: DS.ink3, mt: "3px" }}>{courseNumber}</Typography>
          )}
          {desc && (
            <Typography sx={{ fontSize: 11.5, color: DS.ink3, mt: "6px", lineHeight: 1.5,
              display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
              {desc}
            </Typography>
          )}
        </Box>
      </Box>
      <Box sx={{ height: "1px", bgcolor: DS.line2 }} />
      <Box sx={{ p: "9px 13px", display: "flex", flexWrap: "wrap", gap: "5px" }}>
        {category && (
          <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "4px",
            fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em",
            p: "3px 8px", borderRadius: "999px",
            bgcolor: catBg, color: catColor, border: `1px solid ${catColor}33` }}>
            <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: "currentColor" }} />
            {category}
          </Box>
        )}
        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "4px",
          fontSize: "10.5px", fontWeight: 600, color: DS.ink3,
          bgcolor: DS.surface3, p: "3px 8px", borderRadius: "999px" }}>
          <GroupIcon sx={{ fontSize: 11 }} />{seats} seats
        </Box>
      </Box>
    </Box>
  );
}

const mkSession = () => ({
  id: `${Date.now()}-${Math.random()}`,
  name: "", date: "", startTime: "", endTime: "", locKey: "", instructor: "",
  roomId: "", instructorId: "", ahaModuleKeys: [],
});


function SessionCard({ session, index, onChange, onRemove, canRemove, rooms = [], instructors = [], sites = [], primaryKey = "" }) {
  const DS = useDS();
  const upd = (k, v) => onChange({ ...session, [k]: v });
  const virtualClass = isVirtual(primaryKey);
  const locKey = virtualClass ? VIRTUAL : (session.locKey || primaryKey);
  const INP_SX = {
    width: "100%", height: 38, p: "0 12px",
    bgcolor: DS.surface2, border: `1.5px solid ${DS.line}`, borderRadius: "10px",
    fontFamily: "inherit", fontSize: 13, color: DS.ink, outline: "none",
    transition: "border-color .13s, box-shadow .13s",
    "&:focus": { borderColor: DS.primary, boxShadow: `0 0 0 3px ${alpha(DS.primary, 0.08)}`, bgcolor: "#fff" },
    "&::placeholder": { color: DS.ink4 },
  };
  const LBSX = { fontSize: 11.5, fontWeight: 700, color: DS.ink2, mb: "5px",
    display: "flex", alignItems: "center", gap: "4px" };
  return (
    <Box sx={{ flexShrink: 0, bgcolor: "#fff", border: `1.5px solid ${DS.line}`, borderRadius: "13px",
      overflow: "hidden",
      boxShadow: "0 1px 0 rgba(20,24,40,.04),0 1px 3px rgba(20,24,40,.06)",
      transition: "border-color .15s",
      "&:focus-within": { borderColor: alpha(DS.primary, 0.25) } }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between",
        p: "10px 14px", bgcolor: DS.surface2, borderBottom: `1px solid ${DS.line2}` }}>
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: "6px",
          fontSize: 11.5, fontWeight: 700, color: DS.primary,
          bgcolor: DS.primary10, border: `1px solid ${DS.primary50}`,
          p: "3px 10px", borderRadius: "999px" }}>
          <CalendarTodayIcon sx={{ fontSize: 11 }} />
          {session.name ? session.name : `Session ${index + 1}`}
        </Box>
        {canRemove && (
          <Box component="button" type="button" onClick={onRemove}
            sx={{ display: "flex", alignItems: "center", gap: "4px",
              fontFamily: "inherit", fontSize: 11.5, fontWeight: 600, color: DS.ink4,
              bgcolor: "transparent", border: "none", cursor: "pointer", p: "3px 8px",
              borderRadius: "7px", transition: "all .12s",
              "&:hover": { bgcolor: DS.danger50, color: DS.danger } }}>
            <CloseIcon sx={{ fontSize: 10 }} /> Remove
          </Box>
        )}
      </Box>
      <Box sx={{ p: "13px 14px", display: "flex", flexDirection: "column", gap: "11px" }}>
        <Box>
          <Typography sx={LBSX}>
            Session Name
            <Box component="span" sx={{ fontSize: 11, fontWeight: 500, color: DS.ink4 }}> · optional</Box>
          </Typography>
          <Box component="input" placeholder={`e.g. Day ${index + 1} — Classroom Lecture`}
            autoComplete="new-password"
            value={session.name} onChange={(e) => upd("name", e.target.value)} sx={INP_SX} />
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "11px" }}>
          <Box>
            <Typography sx={LBSX}>Date</Typography>
            <Box component="input" type="date" value={session.date}
              onChange={(e) => upd("date", e.target.value)} sx={INP_SX} />
          </Box>
          <Box>
            <Typography sx={LBSX}>Time</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: "7px" }}>
              <Box component="input" type="time" value={session.startTime}
                onChange={(e) => upd("startTime", e.target.value)}
                sx={{ ...INP_SX, flex: 1 }} />
              <Typography sx={{ color: DS.ink4, fontSize: 14, flexShrink: 0, lineHeight: 1 }}>→</Typography>
              <Box component="input" type="time" value={session.endTime}
                onChange={(e) => upd("endTime", e.target.value)}
                sx={{ ...INP_SX, flex: 1 }} />
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "11px" }}>
          <Box>
            <Typography sx={LBSX}>
              Location
              <Box component="span" sx={{ color: DS.live, fontSize: 15, lineHeight: 1 }}>*</Box>
            </Typography>
            <LocationSelect aria-label="Session location" value={locKey} sites={sites} virtualOnly={virtualClass}
              onChange={(v) => onChange({ ...session, locKey: v, roomId: "" })}
              sx={{ ...INP_SX, p: "0 30px 0 12px", cursor: "pointer", appearance: "none", WebkitAppearance: "none" }} />
          </Box>
          {!isVirtual(locKey) && <Box>
            <Typography sx={LBSX}>
              Room
              <Box component="span" sx={{ fontSize: 11, fontWeight: 500, color: DS.ink4 }}> · optional</Box>
            </Typography>
            <Box component="select" value={session.roomId || ""}
              onChange={(e) => upd("roomId", e.target.value || "")}
              sx={{ ...INP_SX, p: "0 30px 0 12px", cursor: "pointer",
                appearance: "none", WebkitAppearance: "none",
                backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23A6ACBA' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round' xmlns='http://www.w3.org/2000/svg'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\")",
                backgroundRepeat: "no-repeat", backgroundPosition: "right 10px center",
                color: session.roomId ? DS.ink : DS.ink4 }}>
              <option value="">No room assigned</option>
              {rooms.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}{r.number ? ` (${r.number})` : ""}{r.capacity != null ? ` · ${r.capacity} seats` : ""}
                </option>
              ))}
            </Box>
          </Box>}
        </Box>
        <Box>
          <Typography sx={LBSX}>
            Instructor
            <Box component="span" sx={{ fontSize: 11, fontWeight: 500, color: DS.ink4 }}> · optional</Box>
          </Typography>
          <Autocomplete
            options={instructors}
            getOptionLabel={(u) => u.fullName || u.name || u.username || ""}
            value={instructors.find((u) => u.id === session.instructorId) || null}
            onChange={(_, v) => { upd("instructorId", v?.id || ""); }}
            size="small"
            renderInput={(params) => (
              <TextField {...params}
                placeholder="Search instructor…"
                inputProps={{ ...params.inputProps, autoComplete: "new-password" }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: 38, borderRadius: "10px", fontSize: 13,
                    bgcolor: DS.surface2,
                    "& fieldset": { borderColor: DS.line, borderWidth: "1.5px" },
                    "&:hover fieldset": { borderColor: DS.line },
                    "&.Mui-focused fieldset": { borderColor: DS.primary, borderWidth: "1.5px" },
                  },
                  "& .MuiInputBase-input": { p: "0 6px", color: DS.ink },
                  "& .MuiInputBase-input::placeholder": { color: DS.ink4, opacity: 1 },
                }}
              />
            )}
          />
        </Box>
      </Box>
    </Box>
  );
}

function CourseSummary({ title, courseNumber, category, seats, sessions }) {
  const DS = useDS();
  const fmtDate = (s) => {
    if (!s) return "";
    const d = new Date(s + "T12:00:00");
    return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  };
  const fmtTime = (t) => {
    if (!t) return "";
    const [h, m] = t.split(":");
    const hr = +h % 12 || 12;
    return `${hr}:${m} ${+h >= 12 ? "PM" : "AM"}`;
  };
  const { bg: catBg, color: catColor } = getCatConfig(category || "", DS.primary);
  const filled = sessions.filter((s) => s.date);
  return (
    <>
      <Typography sx={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase",
        letterSpacing: ".08em", color: DS.ink4 }}>Course</Typography>
      <Box sx={{ bgcolor: "#fff", border: `1.5px solid ${DS.line}`, borderRadius: "13px",
        p: "12px", display: "flex", gap: "10px",
        boxShadow: "0 1px 0 rgba(20,24,40,.04),0 1px 3px rgba(20,24,40,.06)" }}>
        <Box sx={{ width: 34, height: 34, borderRadius: "9px", flexShrink: 0,
          display: "grid", placeItems: "center",
          bgcolor: category ? catBg : DS.surface3, color: category ? catColor : DS.ink4 }}>
          <SchoolIcon sx={{ fontSize: 17 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700,
            color: title ? DS.ink : DS.ink4, fontStyle: title ? "normal" : "italic",
            letterSpacing: "-.01em", lineHeight: 1.3 }}>
            {title || "Untitled course"}
          </Typography>
          {courseNumber && (
            <Typography sx={{ fontSize: "10.5px", fontFamily: "'JetBrains Mono',monospace",
              fontWeight: 600, color: DS.ink3, mt: "2px" }}>{courseNumber}</Typography>
          )}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "5px", mt: "8px" }}>
            {category && (
              <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "4px",
                fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em",
                p: "3px 8px", borderRadius: "999px", bgcolor: catBg, color: catColor,
                border: `1px solid ${catColor}33` }}>
                <Box sx={{ width: 4, height: 4, borderRadius: "50%", bgcolor: "currentColor" }} />
                {category}
              </Box>
            )}
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "4px",
              fontSize: "10px", fontWeight: 600, color: DS.ink3,
              bgcolor: DS.surface3, border: `1px solid ${DS.line}`,
              p: "3px 8px", borderRadius: "999px" }}>
              <GroupIcon sx={{ fontSize: 10 }} />{seats} seats
            </Box>
          </Box>
        </Box>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <Typography sx={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase",
          letterSpacing: ".08em", color: DS.ink4, whiteSpace: "nowrap" }}>Sessions</Typography>
        {filled.length > 0 && (
          <Box component="span" sx={{ fontSize: "10px", fontWeight: 800, p: "1px 7px",
            borderRadius: "999px", bgcolor: DS.primary10, color: DS.primary,
            border: `1px solid ${DS.primary50}` }}>{filled.length}</Box>
        )}
        <Box sx={{ flex: 1, height: "1px", bgcolor: DS.line2 }} />
      </Box>
      {filled.length === 0 ? (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px",
          p: "16px 10px", textAlign: "center",
          border: `1.5px dashed ${DS.line}`, borderRadius: "11px", color: DS.ink4,
          fontSize: 11.5, lineHeight: 1.4 }}>
          <CalendarTodayIcon sx={{ fontSize: 17, color: DS.ink4 }} />
          No sessions yet — fill in dates on the left
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "7px" }}>
          {sessions.map((s, i) => !s.date ? null : (
            <Box key={s.id} sx={{ display: "flex", alignItems: "flex-start", gap: "9px",
              p: "9px 11px", bgcolor: "#fff", border: `1px solid ${DS.line2}`, borderRadius: "10px" }}>
              <Box sx={{ width: 20, height: 20, borderRadius: "50%", flexShrink: 0,
                bgcolor: DS.primary10, border: `1px solid ${DS.primary50}`, color: DS.primary,
                display: "grid", placeItems: "center", fontSize: 10, fontWeight: 800, mt: "1px" }}>
                {i + 1}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {s.name && (
                  <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: DS.primary }}>
                    {s.name}
                  </Typography>
                )}
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: DS.ink }}>
                  {fmtDate(s.date)}
                </Typography>
                {(s.startTime || s.endTime) && (
                  <Typography sx={{ fontSize: 11, color: DS.ink3, mt: "1px" }}>
                    {s.startTime ? fmtTime(s.startTime) : "–"} → {s.endTime ? fmtTime(s.endTime) : "–"}
                  </Typography>
                )}
                {s.location && (
                  <Typography sx={{ fontSize: 11, color: DS.ink4, mt: "1px" }}>{s.location}</Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </>
  );
}

function CourseDialog({ open, onClose, onSave, tenantId }) {
  const DS = useDS();
  const navigate = useNavigate();
  const CATEGORIES = useConfigList("COURSE_CATEGORY", DEFAULT_CATEGORIES);
  const [step,       setStep]       = useState(1);
  const [title,      setTitle]      = useState("");
  const [courseNum,  setCourseNum]  = useState("");
  const [category,   setCategory]   = useState("");
  // The certificates the class issues: the center's enabled types, Default pre-chosen. AHA is not offered here:
  // AHA courses are standardized and come from the Knowledge Bank (Use this course).
  const [certTypes,  setCertTypes]  = useState([]);
  const [chosenCerts, setChosenCerts] = useState(["DEFAULT"]);
  const [seats,      setSeats]      = useState(20);
  const [desc,       setDesc]       = useState("");
  const [, setEnroll]     = useState("open");
  const [prereqCourses, setPrereqCourses] = useState([]);
  const [sessions,   setSessions]   = useState([mkSession()]);
  const [saving,     setSaving]     = useState(false);
  const [successCourse, setSuccessCourse] = useState(null);
  const [createError, setCreateError] = useState("");
  const [rooms,      setRooms]      = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [primaryInstructorId, setPrimaryInstructorId] = useState("");
  const [bulkRoomId, setBulkRoomId] = useState("");
  const [sites,      setSites]      = useState([]);
  const [siteId,     setSiteId]     = useState("");

  useEffect(() => {
    if (open) {
      setSiteId("");
      fetchSites().then((d) => setSites(Array.isArray(d) ? d : [])).catch(() => setSites([]));
      setStep(1);
      setTitle(""); setCourseNum(""); setCategory(""); setSeats(20);
      setDesc(""); setEnroll("open"); setPrereqCourses([]);
      setSessions([mkSession()]);
      setPrimaryInstructorId("");
      setSuccessCourse(null);
      setBulkRoomId("");
      fetchRooms().then(setRooms).catch(() => setRooms([]));
      fetchTenantUsersByRole(tenantId, "INSTRUCTOR").then(setInstructors).catch(() => setInstructors([]));
      setChosenCerts(["DEFAULT"]);
      fetchCertificateTypes()
        .then((types) => setCertTypes((types || []).filter((t) => t.enabled)))
        .catch(() => setCertTypes([]));
    }
  }, [open, tenantId]);

  // AHA classes are not built here: choosing the AHA category points to the standard AHA courses instead.
  const isAhaCategory = category.trim().toUpperCase() === "AHA";
  // The primary location is required: a configured site, or Virtual (a 100% virtual class).
  const canNext = title.trim().length >= 2 && !!siteId && !isAhaCategory;
  const virtualClass = isVirtual(siteId);

  // A session's location defaults to the primary one; a virtual class only has virtual sessions.
  const locKeyOf = (s) => (virtualClass ? VIRTUAL : (s.locKey || siteId));
  // Rooms that hold the class seats (rooms with no capacity set stay in) and sit at the given location.
  const roomsAt = (key) => (isVirtual(key) ? [] : rooms.filter((r) => (r.capacity == null || r.capacity >= seats)
    && (!key || String(r.siteId) === String(key))));
  // Bulk assign only makes sense when every session is at the primary site.
  const fitRooms = roomsAt(siteId);
  // Seats going up, or a session moving to another site/Virtual, un-assigns a room that no longer fits.
  useEffect(() => {
    const stale = (s) => s.roomId && !roomsAt(locKeyOf(s)).some((r) => String(r.id) === String(s.roomId));
    setSessions((ss) => (ss.some(stale) ? ss.map((s) => (stale(s) ? { ...s, roomId: "" } : s)) : ss));
    setBulkRoomId((b) => (b && !fitRooms.some((r) => String(r.id) === String(b)) ? "" : b));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seats, rooms, siteId]);

  const ahaOffered = certTypes.some((t) => t.type === "AHA");
  const chooseCategory = (value) => setCategory(value);
  const goToStandardCourses = () => navigate("/instructor/knowledge-bank?tab=standard");

  const updateSession = (id, data) => setSessions(ss => ss.map(s => s.id === id ? data : s));
  const removeSession = (id)       => setSessions(ss => ss.filter(s => s.id !== id));
  const addSession    = ()         => setSessions(ss => [...ss, mkSession()]);

  const handleSave = async () => {
    if (!canNext || saving) return;
    setSaving(true);
    setCreateError("");
    const warnings = [];
    try {
      const classData = {
        title, courseNumber: courseNum, category,
        availableSeats: seats, description: desc,
        prereqs: prereqCourses.length > 0 ? prereqCourses.map((p) => p.title).join(", ") : "None",
        instructorId: primaryInstructorId || null,
        schedule: [{ start: new Date().toISOString(), end: new Date().toISOString() }],
        useGenericTerms: true, feeMode: "FREE_ALL", feeAmount: 0,
      };
      const sessionsToCreate = sessions.filter(s => s.date);
      const sessionData = sessionsToCreate.map((s, i) => ({
        name: s.name || undefined,
        sessionDate: s.date,
        startTime: s.startTime || undefined,
        endTime: s.endTime || undefined,
        ...sessionLocationFields(locKeyOf(s), sites),
        roomId: s.roomId ? Number(s.roomId) : undefined,
        leadInstructorId: s.instructorId || undefined,
        trackLocation: !virtualClass,
        capacity: seats,
        sessionNumber: i + 1,
        enabled: false,
      }));
      const created = await createClass(tenantId, classData);
      const courseId = created?.id;
      // The class exists from here on: a later step failing must not strand it half-created, so each step
      // continues and anything that didn't save is reported on the success screen to finish from the class.
      const onlyDefault = chosenCerts.length === 1 && chosenCerts[0] === "DEFAULT";
      if (courseId && !onlyDefault) {
        try {
          await saveClassCertificateTypes(courseId, chosenCerts);
        } catch (err) {
          warnings.push(`The class's certificates weren't saved (${err?.response?.data?.message || "try again"}). `
            + "Set them from the class's Roster tab.");
        }
      }
      if (courseId && sessionsToCreate.length > 0) {
        const results = await Promise.allSettled(sessionData.map((session) => createSession(courseId, session)));
        const failed = results.filter((r) => r.status === "rejected").length;
        if (failed > 0) {
          warnings.push(`${failed} of ${sessionsToCreate.length} session${sessionsToCreate.length === 1 ? "" : "s"} `
            + "couldn't be created. Add them from the class's Sessions tab.");
        }
      }

      onSave();
      setSuccessCourse({ title, courseNumber: courseNum, warnings });
    } catch (err) {
      setCreateError(err?.response?.data?.message || "The course couldn't be created. Nothing was saved.");
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setTitle(""); setCourseNum(""); setCategory(""); setSeats(20);
    setDesc(""); setEnroll("open"); setPrereqCourses([]);
    setSessions([mkSession()]);
    setSuccessCourse(null);
    setPrimaryInstructorId("");
    setBulkRoomId("");
  };

  const INP = {
    width: "100%", height: 40, p: "0 14px",
    bgcolor: DS.surface2, border: `1.5px solid ${DS.line}`, borderRadius: "11px",
    fontFamily: "inherit", fontSize: 14, color: DS.ink, outline: "none",
    transition: "border-color .13s, box-shadow .13s, background .13s",
    "&:focus": { borderColor: DS.primary, boxShadow: `0 0 0 3px ${alpha(DS.primary, 0.08)}`, bgcolor: "#fff" },
    "&::placeholder": { color: DS.ink4 },
  };
  const LBSX = { fontSize: 12, fontWeight: 700, color: DS.ink2, mb: "7px",
    display: "flex", alignItems: "center", gap: "5px" };
  const SECHEAD = { display: "flex", alignItems: "center", gap: "10px", mb: 2 };
  const SECLBL = { fontSize: "11px", fontWeight: 700, textTransform: "uppercase",
    letterSpacing: ".08em", color: DS.ink3, whiteSpace: "nowrap" };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false}
      PaperProps={{ sx: {
        width: 800, maxWidth: "100%", height: "calc(100vh - 40px)",
        borderRadius: "20px",
        boxShadow: "0 36px 80px -24px rgba(20,24,40,.34), 0 16px 32px -20px rgba(20,24,40,.18)",
        overflow: "hidden", display: "flex", flexDirection: "column",
      }}}>

      {/* ── Success screen ── */}
      {successCourse && (
        <Box sx={{ px: 5, py: 6, textAlign: "center" }}>
          <Box sx={{ width: 72, height: 72, borderRadius: "20px", bgcolor: "rgba(0,106,96,0.12)",
            display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 3 }}>
            <Box sx={{ width: 44, height: 44, borderRadius: "50%", bgcolor: DS.secondary,
              display: "flex", alignItems: "center", justifyContent: "center" }}>
              <CheckCircleIcon sx={{ color: "#fff", fontSize: 26 }} />
            </Box>
          </Box>
          <Typography variant="h5" fontWeight={800} color={DS.primary} sx={{ mb: 1.5 }}>
            Course Created Successfully!
          </Typography>
          <Typography variant="body2" color={DS.onSurfaceVariant} sx={{ maxWidth: 380, mx: "auto", lineHeight: 1.7 }}>
            The new training curriculum{" "}
            <Box component="span" sx={{ fontWeight: 700, color: DS.onSurface }}>'{successCourse.title}'</Box>
            {successCourse.courseNumber && <> ({successCourse.courseNumber})</>}{" "}
            has been added to the catalog.
          </Typography>
          {(successCourse.warnings || []).length > 0 && (
            <Alert severity="warning" sx={{ mt: 3, textAlign: "left", maxWidth: 480, mx: "auto" }}>
              {successCourse.warnings.map((w) => <Box key={w} component="div">{w}</Box>)}
            </Alert>
          )}
          <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 4 }}>
            <Button variant="contained" onClick={onClose} sx={{
              bgcolor: DS.primary, color: "#fff", fontWeight: 700, textTransform: "none",
              fontSize: "0.95rem", px: 4, py: 1.25, borderRadius: "8px", boxShadow: "none",
              "&:hover": { bgcolor: DS.primaryContainer, boxShadow: "none" } }}>
              Done
            </Button>
            <Button onClick={resetForm} sx={{
              color: DS.primary, fontWeight: 600, textTransform: "none",
              fontSize: "0.95rem", px: 2, borderRadius: "8px",
              "&:hover": { bgcolor: DS.surfaceContainerLow } }}>
              Create Another Course
            </Button>
          </Stack>
        </Box>
      )}

      {/* ── Form ── */}
      {!successCourse && (
        <>
          {createError && <Alert severity="error" sx={{ m: 2 }} onClose={() => setCreateError("")}>{createError}</Alert>}
          {/* Header */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between",
            p: "20px 28px 18px", borderBottom: `1px solid ${DS.line2}`, flexShrink: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "14px" }}>
              <Box sx={{ width: 44, height: 44, borderRadius: "13px", bgcolor: DS.primary10,
                color: DS.primary, display: "grid", placeItems: "center", flexShrink: 0 }}>
                <SchoolIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase",
                  letterSpacing: ".1em", color: DS.ink3 }}>Course Management</Typography>
                <Typography sx={{ fontSize: 19, fontWeight: 800, color: DS.ink,
                  letterSpacing: "-.025em", mt: "2px" }}>Create New Course</Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <IconButton onClick={onClose} size="small"
                sx={{ width: 36, height: 36, borderRadius: "10px",
                  border: `1.5px solid ${DS.line}`, color: DS.ink3,
                  "&:hover": { bgcolor: DS.surface3, color: DS.ink, borderColor: "var(--color-hairline-cool)" } }}>
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          </Box>

          {/* Body: 2-pane */}
          <Box sx={{ display: "flex", flex: 1, minHeight: 0, overflow: "hidden" }}>

            {step === 1 ? (
              <>
                {/* Form pane */}
                <Box sx={{ flex: 1, minWidth: 0, overflowY: "auto", p: "22px 28px",
                  display: "flex", flexDirection: "column", gap: "20px",
                  scrollbarWidth: "thin",
                  "&::-webkit-scrollbar": { width: 6 },
                  "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
                  "&::-webkit-scrollbar-thumb": { bgcolor: DS.line, borderRadius: "999px" } }}>

                  {/* Section: Course identity */}
                  <Box>
                    <Box sx={SECHEAD}>
                      <Typography sx={SECLBL}>Course identity</Typography>
                      <Box sx={{ flex: 1, height: "1px", bgcolor: DS.line2 }} />
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                      <Box>
                        <Typography sx={LBSX}>
                          Course title
                          <Box component="span" sx={{ color: DS.live, fontSize: 15, lineHeight: 1 }}>*</Box>
                        </Typography>
                        <Box component="input"
                          placeholder="e.g. Firefighter I Recertification"
                          value={title} onChange={(e) => setTitle(e.target.value)}
                          autoFocus sx={INP} />
                      </Box>
                      <Box sx={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "14px", alignItems: "end" }}>
                        <Box>
                          <Typography sx={LBSX}>Course number</Typography>
                          <Box component="input" placeholder="e.g. FF-2024-01"
                            value={courseNum} onChange={(e) => setCourseNum(e.target.value)}
                            sx={{ ...INP, fontFamily: "'JetBrains Mono', monospace", fontSize: 13 }} />
                        </Box>
                        <Box>
                          <Typography sx={LBSX}>Available seats</Typography>
                          <SeatStepper value={seats} onChange={setSeats} />
                        </Box>
                      </Box>
                      <Box>
                        <Typography sx={LBSX}>
                          Primary location
                          <Box component="span" sx={{ color: DS.live, fontSize: 15, lineHeight: 1 }}>*</Box>
                        </Typography>
                        <LocationSelect aria-label="Primary location" value={siteId} sites={sites} onChange={setSiteId}
                          sx={{ ...INP, p: "0 36px 0 14px", cursor: "pointer",
                            appearance: "none", WebkitAppearance: "none",
                            backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%23A6ACBA' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round' xmlns='http://www.w3.org/2000/svg'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\")",
                            backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center",
                            color: siteId ? DS.ink : DS.ink4 }} />
                        {virtualClass && (
                          <Typography sx={{ fontSize: 11.5, color: DS.ink4, mt: "5px" }}>
                            100% virtual class — every session will be virtual.
                          </Typography>
                        )}
                      </Box>
                      <Box>
                        <Typography sx={LBSX}>Category</Typography>
                        <Box component="select" aria-label="Category" value={category} onChange={(e) => chooseCategory(e.target.value)}
                          sx={{ ...INP, p: "0 36px 0 14px", cursor: "pointer",
                            appearance: "none", WebkitAppearance: "none",
                            backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%23A6ACBA' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round' xmlns='http://www.w3.org/2000/svg'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\")",
                            backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center",
                            color: category ? DS.ink : DS.ink4 }}>
                          <option value="" style={{ color: DS.ink4 }}>Select a category…</option>
                          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                        </Box>
                      </Box>
                      <Box>
                        <CertificateTypesPicker available={certTypes.filter((t) => t.type !== "AHA")} value={chosenCerts} onChange={setChosenCerts} />
                      </Box>
                      <Box>
                        <Typography sx={LBSX}>Class instructor <Box component="span" sx={{ fontWeight: 500 }}>· optional</Box></Typography>
                        <Autocomplete options={instructors}
                          getOptionLabel={(u) => u.fullName || u.name || u.username || ""}
                          value={instructors.find((u) => String(u.id) === String(primaryInstructorId)) || null}
                          onChange={(_, instructor) => setPrimaryInstructorId(instructor?.id || "")}
                          size="small" renderInput={(params) => <TextField {...params} placeholder="Search instructors" />} />
                      </Box>
                      {isAhaCategory && (
                        <Alert severity="info" data-testid="aha-standard-message"
                          action={ahaOffered ? (
                            <Button color="inherit" size="small" onClick={goToStandardCourses}>Open Standard courses</Button>
                          ) : null}>
                          {ahaOffered
                            ? "AHA courses are standardized and provided for you. Find them in the Knowledge Bank under Standard courses, then choose Use this course to schedule yours."
                            : "AHA courses are standardized and provided for you, but AHA is not turned on for this training center. Turn on Certifications offered for AHA in your settings first."}
                        </Alert>
                      )}
                      <Box>
                        <Typography sx={LBSX}>
                          Description
                          <Box component="span" sx={{ fontSize: 11, fontWeight: 500, color: DS.ink4 }}>{" · optional"}</Box>
                        </Typography>
                        <Box component="textarea" placeholder="Outline the learning objectives and course overview…"
                          rows={3} value={desc} onChange={(e) => setDesc(e.target.value)}
                          sx={{ width: "100%", p: "11px 14px", resize: "vertical", minHeight: 80,
                            bgcolor: DS.surface2, border: `1.5px solid ${DS.line}`, borderRadius: "11px",
                            fontFamily: "inherit", fontSize: 14, color: DS.ink, outline: "none", lineHeight: 1.5,
                            transition: "border-color .13s, box-shadow .13s, background .13s",
                            "&:focus": { borderColor: DS.primary, boxShadow: `0 0 0 3px ${alpha(DS.primary, 0.08)}`, bgcolor: "#fff" },
                            "&::placeholder": { color: DS.ink4 } }} />
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{ height: "1px", bgcolor: DS.line2 }} />

                  {/* Section: Enrollment settings */}
                  <Box>
                    <Box sx={SECHEAD}>
                      <Typography sx={SECLBL}>Enrollment settings</Typography>
                      <Box sx={{ flex: 1, height: "1px", bgcolor: DS.line2 }} />
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                      <Box>
                        <Typography sx={LBSX}>
                          Prerequisites
                          <Box component="span" sx={{ fontSize: 11, fontWeight: 500, color: DS.ink4 }}>{" · optional"}</Box>
                        </Typography>
                        <PrereqCourseSelect
                          tenantId={tenantId}
                          selected={prereqCourses}
                          onChange={setPrereqCourses}
                        />
                      </Box>
                    </Box>
                  </Box>

                </Box>

                {/* Preview pane */}
                <Box sx={{ width: 244, flexShrink: 0, borderLeft: `1px solid ${DS.line2}`,
                  bgcolor: DS.surface2, p: "20px 18px",
                  display: "flex", flexDirection: "column", gap: "14px", overflowY: "auto" }}>
                  <Typography sx={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase",
                    letterSpacing: ".08em", color: DS.ink4 }}>Live preview</Typography>
                  <CourseLivePreview title={title} courseNumber={courseNum} category={category} seats={seats} desc={desc} />
                  <Typography sx={{ fontSize: 11, color: DS.ink4, lineHeight: 1.5, textAlign: "center" }}>
                    How this course will appear in the Manage Courses table after creation.
                  </Typography>
                </Box>
              </>
            ) : (
              <>
                {/* Sessions pane */}
                <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, overflowY: "auto", p: "20px 28px",
                  display: "flex", flexDirection: "column", gap: "12px",
                  scrollbarWidth: "thin",
                  "&::-webkit-scrollbar": { width: 6 },
                  "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
                  "&::-webkit-scrollbar-thumb": { bgcolor: DS.line, borderRadius: "999px" } }}>

                  {/* Bulk room assign */}
                  {!virtualClass && fitRooms.length > 0 && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: "10px",
                      p: "10px 14px", bgcolor: DS.primary10,
                      border: `1.5px solid ${DS.primary50}`, borderRadius: "11px" }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: DS.primary, whiteSpace: "nowrap" }}>
                        Assign room to all sessions:
                      </Typography>
                      <Box component="select" value={bulkRoomId}
                        onChange={(e) => {
                          const rid = e.target.value;
                          setBulkRoomId(rid);
                          if (rid) setSessions(ss => ss.map(s => ({ ...s, roomId: rid })));
                        }}
                        sx={{ flex: 1, height: 34, p: "0 28px 0 10px", borderRadius: "8px",
                          border: `1.5px solid ${DS.primary50}`, bgcolor: "#fff",
                          fontFamily: "inherit", fontSize: 12, fontWeight: 600, color: DS.ink,
                          outline: "none", cursor: "pointer",
                          appearance: "none", WebkitAppearance: "none",
                          backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23A6ACBA' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round' xmlns='http://www.w3.org/2000/svg'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\")",
                          backgroundRepeat: "no-repeat", backgroundPosition: "right 8px center" }}>
                        <option value="">— select room —</option>
                        {fitRooms.map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.name}{r.number ? ` (${r.number})` : ""}
                          </option>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {sessions.map((s, i) => (
                    <SessionCard
                      key={s.id} session={s} index={i}
                      onChange={(data) => updateSession(s.id, data)}
                      onRemove={() => removeSession(s.id)}
                      canRemove={sessions.length > 1}
                      rooms={roomsAt(locKeyOf(s))}
                      instructors={instructors}
                      sites={sites} primaryKey={siteId}
                    />
                  ))}
                  <Box component="button" type="button" onClick={addSession}
                    sx={{ flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                      height: 44, width: "100%", p: 0,
                      borderRadius: "12px", border: `1.5px dashed ${DS.line}`, bgcolor: "transparent",
                      fontFamily: "inherit", fontSize: 13, fontWeight: 700, color: DS.ink3,
                      cursor: "pointer", transition: "all .13s",
                      "&:hover": { bgcolor: DS.surface2, borderColor: "var(--color-hairline-cool)", color: DS.ink } }}>
                    <AddIcon sx={{ fontSize: 16 }} /> Add another session
                  </Box>
                </Box>

                {/* Summary pane */}
                <Box sx={{ width: 244, flexShrink: 0, minHeight: 0, borderLeft: `1px solid ${DS.line2}`,
                  bgcolor: DS.surface2, p: "20px 18px",
                  display: "flex", flexDirection: "column", gap: "14px", overflowY: "auto" }}>
                  <CourseSummary title={title} courseNumber={courseNum} category={category} seats={seats}
                    sessions={sessions.map((x) => ({ ...x, location: locationText(locKeyOf(x), sites) }))} />
                </Box>
              </>
            )}

          </Box>

          {/* Footer */}
          <Box sx={{ p: "14px 28px", borderTop: `1px solid ${DS.line2}`, bgcolor: DS.surface2,
            display: "flex", alignItems: "center", justifyContent: "space-between",
            gap: "16px", flexShrink: 0 }}>
            {step === 1 ? (
              <>
                <Typography sx={{ fontSize: "11.5px", color: DS.ink4, lineHeight: 1.4, maxWidth: 340 }}>
                  Sessions can be added now or after creation.
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: "9px", flexShrink: 0 }}>
                  <Button onClick={onClose} sx={{
                    height: 40, px: "18px", borderRadius: "11px",
                    border: `1.5px solid ${DS.line}`, bgcolor: "#fff",
                    fontFamily: "inherit", fontSize: 14, fontWeight: 700, color: DS.ink2,
                    textTransform: "none", minWidth: 0,
                    "&:hover": { bgcolor: DS.surface3, color: DS.ink, borderColor: "var(--color-hairline-cool)" } }}>
                    Cancel
                  </Button>
                  <Button onClick={handleSave} disabled={!canNext || saving}
                    sx={{ height: 40, px: "18px", borderRadius: "11px",
                      border: `1.5px solid ${DS.line}`, bgcolor: "#fff",
                      fontFamily: "inherit", fontSize: 14, fontWeight: 700, color: DS.ink2,
                      textTransform: "none", minWidth: 0,
                      "&:hover:not(.Mui-disabled)": { bgcolor: DS.surface3, color: DS.ink, borderColor: "var(--color-hairline-cool)" },
                      "&.Mui-disabled": { opacity: 0.38, cursor: "not-allowed" } }}>
                    {saving ? "Creating…" : "Create"}
                  </Button>
                  <Button onClick={() => setStep(2)} disabled={!canNext}
                    sx={{ height: 40, px: "20px", borderRadius: "11px", border: 0,
                      bgcolor: DS.primary, color: "#fff", fontFamily: "inherit", fontSize: 14,
                      fontWeight: 700, textTransform: "none",
                      display: "inline-flex", alignItems: "center", gap: "7px",
                      boxShadow: `0 4px 12px -4px ${alpha(DS.primary, 0.52)}`,
                      "&:hover:not(.Mui-disabled)": { bgcolor: DS.primary700,
                        transform: "translateY(-1px)", boxShadow: `0 6px 18px -4px ${alpha(DS.primary, 0.58)}` },
                      "&.Mui-disabled": { opacity: 0.38, cursor: "not-allowed", boxShadow: "none",
                        transform: "none", bgcolor: DS.primary, color: "#fff" } }}>
                    Add Sessions <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                  </Button>
                </Box>
              </>
            ) : (
              <>
                <Typography sx={{ fontSize: "11.5px", color: DS.ink4, lineHeight: 1.4, maxWidth: 340 }}>
                  Sessions created here will be available immediately after the course is created.
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: "9px", flexShrink: 0 }}>
                  <Button onClick={() => setStep(1)} sx={{
                    height: 40, px: "16px", borderRadius: "11px",
                    border: `1.5px solid ${DS.line}`, bgcolor: "#fff",
                    fontFamily: "inherit", fontSize: 14, fontWeight: 700, color: DS.ink2,
                    textTransform: "none", display: "inline-flex", alignItems: "center", gap: "7px",
                    "&:hover": { bgcolor: DS.surface3, color: DS.ink, borderColor: "var(--color-hairline-cool)" } }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg> Back
                  </Button>
                  <Button onClick={handleSave} sx={{
                    height: 40, px: "14px", borderRadius: "11px", border: 0, bgcolor: "transparent",
                    fontFamily: "inherit", fontSize: "13.5px", fontWeight: 600, color: DS.ink3,
                    textTransform: "none",
                    "&:hover": { color: DS.ink, bgcolor: DS.surface3 } }}>
                    Skip for now
                  </Button>
                  <Button onClick={handleSave} disabled={saving}
                    sx={{ height: 40, px: "20px", borderRadius: "11px", border: 0,
                      bgcolor: DS.primary, color: "#fff", fontFamily: "inherit", fontSize: 14,
                      fontWeight: 700, textTransform: "none",
                      display: "inline-flex", alignItems: "center", gap: "7px",
                      boxShadow: `0 4px 12px -4px ${alpha(DS.primary, 0.52)}`,
                      "&:hover:not(.Mui-disabled)": { bgcolor: DS.primary700,
                        transform: "translateY(-1px)", boxShadow: `0 6px 18px -4px ${alpha(DS.primary, 0.58)}` },
                      "&.Mui-disabled": { opacity: 0.38, cursor: "not-allowed", boxShadow: "none",
                        transform: "none", bgcolor: DS.primary, color: "#fff" } }}>
                    {saving ? "Creating…" : <><CheckCircleIcon sx={{ fontSize: 14 }} /> Create Course</>}
                  </Button>
                </Box>
              </>
            )}
          </Box>

        </>
      )}
    </Dialog>
  );
}

// ── Template Card helpers ─────────────────────────────────────────────────────
const CAT_META_STATIC = {
  Firefighter: { stripe: "#C2410C", bg: "rgba(194,65,12,.10)", color: "#C2410C", label: "Firefighter" },
  EMS:         { stripe: "var(--color-primary)", bg: "rgba(37,99,235,.10)",  color: "var(--color-primary)", label: "EMS" },
  Hazmat:      { stripe: "#B5651D", bg: "rgba(181,101,29,.10)", color: "#B5651D", label: "Hazmat" },
  "Search & Rescue": { stripe: "#15803D", bg: "rgba(21,128,61,.10)", color: "#15803D", label: "S&R" },
};
function catMeta(cat, tPrimary = "var(--color-primary)") {
  const dynamic = { ...CAT_META_STATIC, "Fire Officer": { stripe: tPrimary, bg: alpha(tPrimary, 0.06), color: tPrimary, label: "Fire Officer" } };
  return dynamic[cat] || { stripe: tPrimary, bg: alpha(tPrimary, 0.06), color: tPrimary, label: cat || "Other" };
}

function CatIcon({ cat, size = 20 }) {
  const s = { width: size, height: size, fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" };
  if (cat === "Firefighter") return <svg viewBox="0 0 24 24" style={s}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>;
  if (cat === "EMS") return <svg viewBox="0 0 24 24" style={s}><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>;
  if (cat === "Hazmat") return <svg viewBox="0 0 24 24" style={s}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>;
  if (cat === "Search & Rescue") return <svg viewBox="0 0 24 24" style={s}><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5M2 12l10 5 10-5"/></svg>;
  return <svg viewBox="0 0 24 24" style={s}><path d="M22 10v6M2 10l10-5 10 5-10 5z"/></svg>;
}

function TemplateCard({ t, cloningId, onClone, onUnmark }) {
  const DS = useDS();
  const meta = catMeta(t.category, DS.primary);
  const sessionCount = Array.isArray(t.schedule) ? t.schedule.length : (t.sessions || 0);
  const [menuEl, setMenuEl] = React.useState(null);

  return (
    <Box sx={{ bgcolor: "#fff", border: "1px solid var(--color-hairline-cloud)", borderRadius: "12px",
      display: "flex", flexDirection: "column", overflow: "hidden",
      transition: "border-color .14s, box-shadow .14s, transform .14s",
      "&:hover": { borderColor: "#cfcfdb", boxShadow: "0 10px 15px -3px rgba(0,0,0,.08), 0 4px 6px -4px rgba(0,0,0,.06)", transform: "translateY(-2px)" },
    }}>
      {/* Category stripe */}
      <Box sx={{ height: "4px", flexShrink: 0, bgcolor: meta.stripe }} />

      <Box sx={{ p: "16px 18px 18px", display: "flex", flexDirection: "column", gap: "11px", flex: 1 }}>
        {/* Top row */}
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: "11px" }}>
          <Box sx={{ width: 40, height: 40, borderRadius: "10px", flexShrink: 0, display: "grid",
            placeItems: "center", bgcolor: meta.bg, color: meta.color }}>
            <CatIcon cat={t.category} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
              <Typography sx={{ fontSize: "14.5px", fontWeight: 600, color: "var(--color-ink)",
                lineHeight: 1.25 }}>{t.title}</Typography>
              {t.version && (
                <Box component="span" sx={{ fontSize: "10px", fontWeight: 700, px: "6px", py: "1px",
                  borderRadius: "6px", bgcolor: "var(--color-primary-10)", color: "var(--color-primary)", border: "1px solid #BFDBFE",
                  letterSpacing: ".02em", flexShrink: 0 }}>
                  v{t.version}
                </Box>
              )}
            </Box>
            {t.courseNumber && (
              <Typography sx={{ fontSize: "11px", fontFamily: "'JetBrains Mono',monospace",
                fontWeight: 600, color: "var(--color-ink-3)", mt: "2px" }}>{t.courseNumber}</Typography>
            )}
          </Box>
          <Box component="button" aria-label={`Template actions for ${t.title}`} onClick={(e) => setMenuEl(e.currentTarget)}
            sx={{ width: 32, height: 32, borderRadius: "6px", border: "1px solid var(--color-hairline-cloud)",
              bgcolor: "#fff", display: "grid", placeItems: "center", cursor: "pointer",
              color: "var(--color-ink)", opacity: 0.7, transition: "all .12s", flexShrink: 0,
              "&:hover": { bgcolor: "var(--color-surface-2)", opacity: 1 } }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="5" r="1"/><circle cx="12" cy="12" r="1"/><circle cx="12" cy="19" r="1"/></svg>
          </Box>
        </Box>

        {/* Category pill */}
        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "5px",
          fontSize: "11px", fontWeight: 700, px: "9px", py: "3px", borderRadius: "999px",
          alignSelf: "flex-start", bgcolor: meta.bg, color: meta.color,
          border: `1px solid ${meta.color}33` }}>
          <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: "currentColor" }} />
          {meta.label}
        </Box>

        {/* Description */}
        {t.description && (
          <Typography sx={{ fontSize: "12.5px", color: "var(--color-ink-3)", lineHeight: 1.55,
            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            {t.description}
          </Typography>
        )}

        {/* Specs strip */}
        <Box sx={{ display: "flex", py: "10px", borderTop: "1px solid var(--color-surface-press-light)",
          borderBottom: "1px solid var(--color-surface-press-light)" }}>
          {[
            sessionCount ? { val: sessionCount, lbl: sessionCount === 1 ? "Session" : "Sessions" } : null,
            { val: t.capacity || "—", lbl: "Capacity" },
          ].filter(Boolean).map((spec, i, arr) => (
            <Box key={spec.lbl} sx={{ flex: 1, display: "flex", flexDirection: "column",
              alignItems: "center", gap: "2px", position: "relative",
              "&:not(:last-child)::after": { content: '""', position: "absolute", right: 0,
                top: "50%", transform: "translateY(-50%)", width: "1px", height: "20px",
                bgcolor: "var(--color-surface-press-light)" } }}>
              <Typography sx={{ fontSize: "15px", fontWeight: 700, color: "var(--color-ink)" }}>{spec.val}</Typography>
              <Typography sx={{ fontSize: "9.5px", fontWeight: 700, letterSpacing: ".06em",
                textTransform: "uppercase", color: "var(--color-ink-4)" }}>{spec.lbl}</Typography>
            </Box>
          ))}
        </Box>

        {/* Footer */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "7px", mt: "auto" }}>
          <Box component="button"
            disabled={cloningId === t.id}
            onClick={() => onClone(t.id)}
            sx={{ flex: 1, height: 36, px: "10px", borderRadius: "8px", border: 0,
              bgcolor: DS.primary, color: "#fff", font: "inherit", fontSize: "12.5px",
              fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center",
              justifyContent: "center", gap: "6px",
              transition: "all .13s",
              "&:hover:not(:disabled)": { bgcolor: DS.primary700 },
              "&:disabled": { opacity: 0.6, cursor: "not-allowed" },
            }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            {cloningId === t.id ? "Creating…" : "Schedule Class"}
          </Box>
        </Box>
      </Box>

      {/* Kebab menu */}
      <Menu anchorEl={menuEl} open={Boolean(menuEl)} onClose={() => setMenuEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { borderRadius: "12px", border: "1px solid var(--color-hairline-cloud)",
          boxShadow: "0 8px 24px -10px rgba(20,24,40,.14)", minWidth: 180 } }}>
        <MenuItem onClick={() => { onClone(t.id); setMenuEl(null); }}
          sx={{ fontSize: "13.5px", fontWeight: 600, color: "var(--color-ink)", py: "10px", px: "16px" }}>
          Schedule Class
        </MenuItem>
        <MenuItem onClick={() => { onUnmark(t.id); setMenuEl(null); }}
          sx={{ fontSize: "13.5px", fontWeight: 600, color: "#B42318", py: "10px", px: "16px" }}>
          Delete Template
        </MenuItem>
      </Menu>
    </Box>
  );
}

// "Import from template": pick a published class template, then continue in the schedule-from-template flow.
function ImportFromTemplateDialog({ open, templates, loading, onPick, onClose }) {
  const usable = templates.filter((t) => t.status !== "DRAFT");
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Import from template</DialogTitle>
      <DialogContent>
        {loading ? <LinearProgress /> : usable.length === 0 ? (
          <Typography sx={{ fontSize: 14, color: "var(--color-ink)", opacity: 0.7 }}>
            No published templates yet. Save a class to the Course Bank and publish it, and it will show up here.
          </Typography>
        ) : (
          <List disablePadding>
            {usable.map((t) => (
              <ListItemButton key={t.id} onClick={() => onPick(t.id)}>
                <ListItemText primary={t.title} secondary={t.category || null} />
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Cancel</Button></DialogActions>
    </Dialog>
  );
}

function TemplatesPane({ templates, loading, cloningId, onClone, onUnmark,
  tplSearch, setTplSearch, tplCatFilter, setTplCatFilter }) {
  const DS = useDS();
  const cats = [...new Set(templates.map(t => t.category).filter(Boolean))];

  const filtered = templates.filter(t => {
    const matchSearch = !tplSearch || t.title?.toLowerCase().includes(tplSearch.toLowerCase())
      || t.courseNumber?.toLowerCase().includes(tplSearch.toLowerCase());
    const matchCat = !tplCatFilter || t.category === tplCatFilter;
    return matchSearch && matchCat;
  });

  const CAT_COUNTS = cats.reduce((acc, c) => {
    acc[c] = templates.filter(t => t.category === c).length;
    return acc;
  }, {});

  if (loading) return <LinearProgress />;

  return (
    <Box>
      {/* Toolbar */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: "20px", flexWrap: "wrap" }}>
        {/* Search */}
        <Box sx={{ position: "relative", flex: 1, minWidth: 200, maxWidth: 320 }}>
          <Box sx={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
            color: "var(--color-ink-4)", pointerEvents: "none", display: "flex" }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          </Box>
          <Box component="input" value={tplSearch} onChange={e => setTplSearch(e.target.value)}
            placeholder="Search templates…"
            sx={{ width: "100%", height: 38, pl: "36px", pr: "14px", bgcolor: "#fff",
              border: "1px solid var(--color-hairline-cloud)", borderRadius: "6px", font: "inherit",
              fontSize: "13.5px", color: "var(--color-ink)", outline: "none",
              transition: "border-color .13s",
              "&:focus": { borderColor: DS.primary },
              "&::placeholder": { color: "var(--color-ink-4)" },
            }} />
        </Box>

        {/* Category chips */}
        <Box sx={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
          {[{ key: "", label: "All", count: templates.length }, ...cats.map(c => ({ key: c, label: c, count: CAT_COUNTS[c] }))].map(chip => (
            <Box component="button" key={chip.key} onClick={() => setTplCatFilter(chip.key)}
              sx={{ height: 36, px: "13px", borderRadius: "6px", font: "inherit",
                fontSize: "12.5px", fontWeight: 600, cursor: "pointer", display: "inline-flex",
                alignItems: "center", gap: "5px", transition: "all .12s",
                border: tplCatFilter === chip.key ? `1px solid ${alpha(DS.primary, 0.2)}` : "1px solid var(--color-hairline-cloud)",
                bgcolor: tplCatFilter === chip.key ? DS.primary10 : "#fff",
                color: tplCatFilter === chip.key ? DS.primary : "var(--color-ink)",
                opacity: tplCatFilter === chip.key ? 1 : 0.65,
                "&:hover": { bgcolor: tplCatFilter !== chip.key ? "var(--color-surface-2)" : undefined, opacity: tplCatFilter !== chip.key ? 0.9 : 1 },
              }}>
              {chip.label}
              <Box component="span" sx={{ fontSize: "10.5px", fontWeight: 700, opacity: 0.7 }}>{chip.count}</Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Content */}
      {filtered.length === 0 ? (
        <Box sx={{ bgcolor: "#fff", border: "1px solid var(--color-hairline-cloud)", borderRadius: "12px",
          p: "48px 24px", textAlign: "center" }}>
          <Box sx={{ width: 48, height: 48, borderRadius: "14px", bgcolor: DS.primary10,
            display: "grid", placeItems: "center", mx: "auto", mb: 2, color: DS.primary }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
          </Box>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: "var(--color-ink)", mb: "6px" }}>
            {templates.length === 0 ? "No templates yet" : "No templates match your filters"}
          </Typography>
          <Typography sx={{ fontSize: 13, color: "var(--color-ink-3)", maxWidth: "40ch", mx: "auto" }}>
            {templates.length === 0
              ? 'Mark any class as a template using the "⋮" menu in the Classes tab.'
              : "Try clearing your search or category filter."}
          </Typography>
        </Box>
      ) : (
        <Box>
          {/* Group label */}
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: "14px" }}>
            <Typography sx={{ fontSize: "13.5px", fontWeight: 700, color: "var(--color-ink)" }}>
              Active Templates
            </Typography>
            <Box component="span" sx={{ fontSize: "11px", fontWeight: 600, px: "8px", py: "2px",
              borderRadius: "999px", bgcolor: "var(--color-surface-2)", color: "var(--color-ink)", opacity: 0.7 }}>{filtered.length}</Box>
            <Box sx={{ flex: 1, height: "1px", bgcolor: "var(--color-surface-press-light)" }} />
          </Box>

          {/* 3-column grid */}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }, gap: "14px" }}>
            {filtered.map(t => (
              <TemplateCard key={t.id} t={t} cloningId={cloningId} onClone={onClone} onUnmark={onUnmark} />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────
export default function CourseManagementPage() {
  const DS = useDS();
  const navigate = useNavigate();
  const { tenantId: selectedTenantId } = useActiveTenant();

  const [courses, setCourses] = useState([]);
  const [enrollmentMap, setEnrollmentMap] = useState({});
  const [instructorMap, setInstructorMap] = useState({});
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);

  // filters
  const [searchText, setSearchText] = useState("");
  const [dateFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  // pagination
  const [page, setPage] = useState(1);

  // dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [, setEditingCourse] = useState(null);

  // templates tab
  const [activeTab, setActiveTab] = React.useState(0);
  const [templates, setTemplates] = React.useState([]);
  const [templatesLoading, setTemplatesLoading] = React.useState(false);
  const [cloningId] = React.useState(null);
  const [tplSearch, setTplSearch] = React.useState("");
  const [tplCatFilter, setTplCatFilter] = React.useState("");

  // actions menu
  const [menuAnchor, setMenuAnchor] = React.useState(null);
  const [menuCourse, setMenuCourse] = React.useState(null);
  const [deleteCourse, setDeleteCourse] = React.useState(null);
  const [deleteTemplate, setDeleteTemplate] = React.useState(null);
  const [importOpen, setImportOpen] = React.useState(false);
  const [toast, setToast] = React.useState("");

  const load = useCallback(async () => {
    if (!selectedTenantId) { setLoading(false); return; }
    setLoading(true);
    try {
      // Load users once and build instructor lookup
      const allUsers = await fetchUsers().catch(() => []);
      fetchTenantUsersByRole(selectedTenantId, "INSTRUCTOR").then(setInstructors).catch(() => setInstructors([]));
      const iMap = {};
      allUsers.forEach((u) => {
        iMap[u.id] = u.fullName || u.name || u.username || u.id;
      });
      setInstructorMap(iMap);

      const all = await fetchClasses(selectedTenantId, "");
      setCourses(all);

      // Enrollment counts come back on each class already (studentCount/capacity) —
      // no need for a per-class registrations round trip.
      const map = {};
      all.forEach((c) => {
        map[c.id] = { enrolled: c.studentCount ?? 0, capacity: c.capacity ?? c.availableSeats };
      });
      setEnrollmentMap(map);
    } catch (err) {
      console.error("CourseManagementPage load error:", err);
    } finally {
      setLoading(false);
    }
  }, [selectedTenantId]);

  const loadTemplates = useCallback(async () => {
    if (!selectedTenantId) return;
    setTemplatesLoading(true);
    try {
      const data = await fetchClassTemplates(selectedTenantId);
      setTemplates(data);
    } finally {
      setTemplatesLoading(false);
    }
  }, [selectedTenantId]);

  useEffect(() => {
    if (activeTab === 1) loadTemplates();
  }, [activeTab, loadTemplates]);

  const handleClone = (templateId) => {
    navigate(`/coordinator/schedule-from-template/${templateId}?tenantId=${selectedTenantId}`);
  };

  const openMenu = (e, course) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuCourse(course);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuCourse(null);
  };

  const flashToast = (message, ms = 2800) => { setToast(message); setTimeout(() => setToast(""), ms); };
  const { save: saveClassToBank, dialog: saveToBankDialog, saving: savingToBank } = useSaveClassToBank({
    tenantId: selectedTenantId,
    api: { fetchClassTemplates, createClassTemplateFromClass, syncClassTemplateFromClass },
    onDone: (message) => { flashToast(message); loadTemplates(); },
    onError: (message) => flashToast(message, 4000),
  });

  const handleSaveToBank = async () => {
    const course = menuCourse;
    closeMenu();
    if (course) await saveClassToBank(course);
  };

  useEffect(() => {
    load();
  }, [load]);

  const now = new Date();

  // ── Categories ─────────────────────────────────────────────────────────────
  const categories = [
    ...new Set(courses.map((c) => c.category).filter(Boolean)),
  ];

  // ── Filtered + paginated ───────────────────────────────────────────────────
  const filtered = courses.filter((c) => {
    const enroll = enrollmentMap[c.id] || { enrolled: 0, capacity: c.availableSeats };
    if (searchText) {
      const primaryInstructorId = c.leadInstructorId || c.instructorId;
      const instrName = instructorMap[primaryInstructorId] || "";
      const q = searchText.toLowerCase();
      if (!c.title.toLowerCase().includes(q) && !instrName.toLowerCase().includes(q)) {
        return false;
      }
    }
    if (categoryFilter !== "all" && c.category !== categoryFilter) return false;
    if (dateFilter !== "all") {
      const d = new Date(c.date);
      const days = (d - now) / (1000 * 60 * 60 * 24);
      if (dateFilter === "30" && (days < 0 || days > 30)) return false;
      if (dateFilter === "60" && (days < 0 || days > 60)) return false;
      if (dateFilter === "90" && (days < 0 || days > 90)) return false;
    }
    if (statusFilter !== "all") {
      const st = getCourseStatus(c, enroll);
      if (statusFilter !== st) return false;
    }
    return true;
  });

  const totalPages = Math.ceil(filtered.length / COURSES_PER_PAGE);
  const paginated = filtered.slice(
    (page - 1) * COURSES_PER_PAGE,
    page * COURSES_PER_PAGE,
  );

  if (!loading && !selectedTenantId) {
    return (
      <Box sx={{ minHeight: "100vh" }}>
        <Box sx={{ maxWidth: 1200, mx: "auto", px: { xs: 2, sm: 4 }, py: { xs: 2, sm: 3.5 } }}>
          <NoTenantSelected />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <Box sx={{ maxWidth: 1200, mx: "auto", px: { xs: 2, sm: 4 }, py: { xs: 2, sm: 3.5 }, pb: 12 }}>
        {/* Page header */}
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, mb: 2.75, flexWrap: "wrap" }}>
          <Box>
            <Typography sx={{ fontSize: { xs: 24, sm: 30 }, fontWeight: 600, letterSpacing: "-.01em", color: "var(--color-ink)", lineHeight: 1.15, fontFamily: "var(--font-display)" }}>
              Manage Courses
            </Typography>
            <Typography sx={{ mt: "7px", fontSize: 14, color: "var(--color-ink)", opacity: 0.6, maxWidth: "56ch" }}>
              Oversee certification lifecycles, track enrollment, and manage courses across the regional network.
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "9px", flexShrink: 0, pt: "2px", flexWrap: "wrap" }}>
            <Button
              startIcon={<FileDownloadIcon sx={{ fontSize: 14 }} />}
              onClick={() => navigate("/transcript")}
              sx={{ height: 38, px: 2, borderRadius: "8px", fontSize: 13, fontWeight: 500, textTransform: "none",
                bgcolor: "#fff", color: "var(--color-ink)", border: "1px solid var(--color-hairline-cloud)",
                "&:hover": { bgcolor: "var(--color-surface-2)" } }}>
              Export Report
            </Button>
            <Button
              startIcon={<FileDownloadIcon sx={{ fontSize: 14 }} />}
              onClick={() => { setImportOpen(true); loadTemplates(); }}
              sx={{ height: 38, px: 2, borderRadius: "8px", fontSize: 13, fontWeight: 500, textTransform: "none",
                bgcolor: "#fff", color: "var(--color-ink)", border: "1px solid var(--color-hairline-cloud)",
                "&:hover": { bgcolor: "var(--color-surface-2)" } }}>
              Import from Template
            </Button>
            <Button
              startIcon={<AddIcon sx={{ fontSize: 13 }} />}
              onClick={() => { setEditingCourse(null); setDialogOpen(true); }}
              sx={{ height: 38, px: 2, borderRadius: "8px", fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".2px",
                bgcolor: DS.primary, color: "#fff", border: 0,
                "&:hover": { bgcolor: DS.primary700 } }}>
              Create New Course
            </Button>
          </Box>
        </Box>

        {/* Pill-style tab bar */}
        <Box sx={{ display: "flex", gap: "2px", p: "3px", bgcolor: "var(--color-surface-2)",
          borderRadius: "10px", mb: 2.25, width: "fit-content" }}>
          {[
            { label: "Classes", icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg>, count: courses.length },
            { label: "Templates", icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>, count: templates.length },
          ].map((t, i) => (
            <Box component="button" key={t.label} onClick={() => setActiveTab(i)}
              sx={{ height: 36, px: "16px", borderRadius: "8px", border: 0, font: "inherit",
                fontSize: "13px", fontWeight: 600, cursor: "pointer", display: "inline-flex",
                alignItems: "center", gap: "7px", transition: "all .14s",
                bgcolor: activeTab === i ? "#fff" : "transparent",
                color: activeTab === i ? "var(--color-ink)" : "var(--color-ink)",
                opacity: activeTab === i ? 1 : 0.6,
                boxShadow: activeTab === i ? "0 1px 4px rgba(20,24,40,.12)" : "none",
                "&:hover": { opacity: activeTab !== i ? 0.85 : 1 },
              }}>
              {t.icon}
              {t.label}
              <Box component="span" sx={{ fontSize: "10px", fontWeight: 700, px: "6px", py: "1px",
                borderRadius: "999px",
                bgcolor: activeTab === i ? alpha(DS.primary, 0.08) : "var(--color-surface-2)",
                color: activeTab === i ? DS.primary : "var(--color-ink)",
                opacity: activeTab === i ? 1 : 0.7,
              }}>{t.count}</Box>
            </Box>
          ))}
        </Box>

        {/* Table card */}
        <Box sx={{ display: activeTab === 0 ? "block" : "none", bgcolor: "#fff", border: "1px solid var(--color-hairline-cloud)", borderRadius: "12px",
          overflow: "hidden" }}>

          {/* Filter bar */}
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px", px: "20px", py: "14px",
            borderBottom: "1px solid var(--color-surface-press-light)", bgcolor: "var(--color-surface-2)", flexWrap: "wrap" }}>
            {/* Search */}
            <Box sx={{ position: "relative", flex: 1, minWidth: 200 }}>
              <SearchIcon sx={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
                fontSize: 15, color: "var(--color-ink)", opacity: 0.4, pointerEvents: "none" }} />
              <Box component="input"
                placeholder="Filter by course name, instructor, or code…"
                value={searchText}
                onChange={(e) => { setSearchText(e.target.value); setPage(1); }}
                sx={{ width: "100%", height: 38, pl: "36px", pr: "14px",
                  bgcolor: "#fff", border: "1px solid var(--color-hairline-cloud)", borderRadius: "6px",
                  fontFamily: "inherit", fontSize: "13.5px", color: "var(--color-ink)", outline: "none",
                  transition: "border-color .13s",
                  "&:focus": { borderColor: DS.primary },
                  "&::placeholder": { color: "var(--color-ink)", opacity: 0.4 },
                }} />
            </Box>
            {/* Filters */}
            <Box sx={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
              <Box component="select" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                sx={{ height: 38, px: "12px", pr: "28px", border: "1px solid var(--color-hairline-cloud)", borderRadius: "6px",
                  fontFamily: "inherit", fontSize: 13, fontWeight: 600, color: "var(--color-ink)",
                  bgcolor: "#fff", appearance: "none", cursor: "pointer", outline: "none",
                  backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23A6ACBA' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round' xmlns='http://www.w3.org/2000/svg'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\")",
                  backgroundRepeat: "no-repeat", backgroundPosition: "right 9px center",
                  "&:focus": { borderColor: DS.primary } }}>
                <option value="all">All Statuses</option>
                <option value="REGISTRATION_OPEN">Open</option>
                <option value="WAITLIST_ONLY">Waitlist</option>
                <option value="COMPLETED">Completed</option>
                <option value="DRAFT">Draft</option>
              </Box>
              <Box component="select" value={categoryFilter} onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}
                sx={{ height: 38, px: "12px", pr: "28px", border: "1px solid var(--color-hairline-cloud)", borderRadius: "6px",
                  fontFamily: "inherit", fontSize: 13, fontWeight: 600, color: "var(--color-ink)",
                  bgcolor: "#fff", appearance: "none", cursor: "pointer", outline: "none",
                  backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23A6ACBA' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round' xmlns='http://www.w3.org/2000/svg'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\")",
                  backgroundRepeat: "no-repeat", backgroundPosition: "right 9px center",
                  "&:focus": { borderColor: DS.primary } }}>
                <option value="all">All Categories</option>
                {categories.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
              </Box>
            </Box>
          </Box>

          {/* Table */}
          {loading ? (
            <LinearProgress />
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              <Table sx={{ width: "100%", borderCollapse: "collapse" }}>
                <TableHead>
                  <TableRow sx={{ borderBottom: "1px solid var(--color-surface-press-light)" }}>
                    {["Course Name", "Instructor", "Next Session", "Category", "Status", "Enrollment", "Actions"].map((h, i) => (
                      <TableCell key={h} sx={{
                        py: "11px", px: "16px",
                        fontSize: "11px", fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase",
                        color: "var(--color-ink)", opacity: 0.5, bgcolor: "var(--color-surface-2)", whiteSpace: "nowrap",
                        ...(i === 0 && { pl: "22px" }),
                        ...(i === 6 && { pr: "22px", textAlign: "right" }),
                        ...(i === 2 && { display: { xs: "none", md: "table-cell" } }),
                        ...(i === 3 && { display: { xs: "none", md: "table-cell" } }),
                      }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ py: 6, textAlign: "center", color: "var(--color-ink-3)" }}>
                        No courses match the current filters.
                      </TableCell>
                    </TableRow>
                  ) : paginated.map((course) => {
                    const enroll = enrollmentMap[course.id] || { enrolled: 0, capacity: course.availableSeats };
                    const status = getCourseStatus(course, enroll);
                    const primaryInstructorId = course.leadInstructorId || course.instructorId;
                    const instrName = instructorMap[primaryInstructorId] || "";
                    const { bg: catBg, color: catColor } = getCatConfig(course.category || "", DS.primary);
                    return (
                      <TableRow key={course.id}
                        onClick={() => navigate(`/course-management/${course.id}`)}
                        sx={{ borderBottom: "1px solid var(--color-surface-press-light)", transition: "background .12s", cursor: "pointer",
                          "&:last-child": { borderBottom: 0 }, "&:hover": { bgcolor: "var(--color-surface-2)" } }}>
                        {/* Course Name */}
                        <TableCell sx={{ py: "14px", pl: "22px", pr: "16px" }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: "13px" }}>
                            <Box sx={{ width: 38, height: 38, borderRadius: "8px", flexShrink: 0,
                              display: "grid", placeItems: "center", bgcolor: catBg, color: catColor }}>
                              <SchoolIcon sx={{ fontSize: 18 }} />
                            </Box>
                            <Box>
                              <Typography sx={{ fontSize: 14, fontWeight: 600, color: "var(--color-ink)" }}>
                                {course.title}
                              </Typography>
                              {course.courseNumber && (
                                <Typography sx={{ fontSize: "11.5px", fontFamily: "'JetBrains Mono',monospace", fontWeight: 600, color: "var(--color-ink-3)", mt: "2px" }}>
                                  {course.courseNumber}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        {/* Instructor */}
                        <TableCell sx={{ py: "14px", px: "16px" }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <InstrAvatar name={instrName} />
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "var(--color-ink-2)" }}>
                              {instrName || "—"}
                            </Typography>
                          </Box>
                        </TableCell>
                        {/* Next Session */}
                        <TableCell sx={{ py: "14px", px: "16px", display: { xs: "none", md: "table-cell" } }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "var(--color-ink-2)" }}>
                            {formatDate(course.date)}
                          </Typography>
                        </TableCell>
                        {/* Category */}
                        <TableCell sx={{ py: "14px", px: "16px", display: { xs: "none", md: "table-cell" } }}>
                          <CategoryPill category={course.category} />
                        </TableCell>
                        {/* Status */}
                        <TableCell sx={{ py: "14px", px: "16px" }}>
                          <StatusPill status={status} />
                        </TableCell>
                        {/* Enrollment */}
                        <TableCell sx={{ py: "14px", px: "16px" }}>
                          <EnrollmentBar enrolled={enroll.enrolled} capacity={enroll.capacity} />
                        </TableCell>
                        {/* Actions */}
                        <TableCell sx={{ py: "14px", pl: "16px", pr: "22px" }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: "6px", justifyContent: "flex-end" }}>
                            <Button
                              size="small"
                              onClick={(e) => { e.stopPropagation(); navigate(`/course-management/${course.id}`); }}
                              sx={{ height: 32, px: "13px", borderRadius: "6px", fontSize: "12.5px", fontWeight: 600,
                                textTransform: "none", bgcolor: DS.primary, color: "#fff", border: "none",
                                whiteSpace: "nowrap",
                                "&:hover": { bgcolor: DS.primary700 } }}>
                              Manage
                            </Button>
                            <IconButton size="small"
                              aria-label={`Actions for ${course.title}`}
                              onClick={(e) => openMenu(e, course)}
                              sx={{ width: 32, height: 32, borderRadius: "6px", border: "1px solid var(--color-hairline-cloud)",
                                bgcolor: "#fff", color: "var(--color-ink)", opacity: 0.7, flexShrink: 0,
                                "&:hover": { bgcolor: "var(--color-surface-2)", opacity: 1 } }}>
                              <MoreVertIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}

          {/* Footer / pagination */}
          {!loading && (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between",
              px: "22px", py: "13px", borderTop: "1px solid var(--color-surface-press-light)", bgcolor: "var(--color-surface-2)",
              flexWrap: "wrap", gap: "10px" }}>
              <Typography sx={{ fontSize: 13, color: "var(--color-ink)", opacity: 0.55, fontWeight: 500 }}>
                Showing{" "}
                <Box component="strong" sx={{ color: "var(--color-ink)", opacity: 1 }}>
                  {filtered.length === 0 ? 0 : Math.min((page - 1) * COURSES_PER_PAGE + 1, filtered.length)}–{Math.min(page * COURSES_PER_PAGE, filtered.length)}
                </Box>
                {" "}of{" "}
                <Box component="strong" sx={{ color: "var(--color-ink)", opacity: 1 }}>{filtered.length}</Box>
                {" "}courses
              </Typography>
              {totalPages > 1 && (
                <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} size="small"
                  sx={{
                    "& .MuiPaginationItem-root": { borderRadius: "6px", height: 32, width: 32, border: "1px solid var(--color-hairline-cloud)", bgcolor: "#fff", fontSize: 13, fontWeight: 600, color: "var(--color-ink)" },
                    "& .Mui-selected": { bgcolor: `${DS.primary} !important`, color: "#fff !important", borderColor: "transparent !important" },
                    "& .MuiPaginationItem-root:hover": { bgcolor: "var(--color-surface-2)" },
                  }} />
              )}
            </Box>
          )}
        </Box>

        {/* Actions Menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={closeMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{ sx: { borderRadius: "12px", border: "1px solid var(--color-hairline-cloud)", boxShadow: "0 8px 24px -10px rgba(20,24,40,.14)", minWidth: 200 } }}
        >
          <MenuItem
            onClick={handleSaveToBank}
            disabled={savingToBank}
            sx={{ fontSize: "13.5px", fontWeight: 600, color: "var(--color-ink)", py: "10px", px: "16px" }}
          >
            {savingToBank ? "Saving…" : "Save to Course Bank"}
          </MenuItem>
          <MenuItem
            onClick={() => { setDeleteCourse(menuCourse); closeMenu(); }}
            sx={{ fontSize: "13.5px", fontWeight: 600, color: "#B42318", py: "10px", px: "16px" }}
          >
            Delete class
          </MenuItem>
        </Menu>

        {saveToBankDialog}

        <ConfirmDialog
          open={!!deleteCourse}
          title="Delete class?"
          message={<>
            <b>“{deleteCourse?.title}”</b> will be permanently deleted, along with its sessions, openings, and everything assigned to it
            (tests, skill sheets, documents). This can't be undone. A class can't be deleted while students are enrolled.
          </>}
          requireText={deleteCourse?.title}
          confirmLabel="Delete class"
          onConfirm={async () => {
            await deleteClass(selectedTenantId, deleteCourse.id);
            setToast("Class deleted");
            setTimeout(() => setToast(""), 2600);
            await load();
          }}
          onClose={() => setDeleteCourse(null)}
        />

        <ConfirmDialog
          open={!!deleteTemplate}
          title="Delete class template?"
          message={<>
            This permanently removes the template <b>“{deleteTemplate?.title}”</b>. Classes already created from it are not affected.
          </>}
          confirmLabel="Delete template"
          onConfirm={async () => {
            await deleteClassTemplate(selectedTenantId, deleteTemplate.id);
            await loadTemplates();
          }}
          onClose={() => setDeleteTemplate(null)}
        />

        <ImportFromTemplateDialog
          open={importOpen}
          templates={templates}
          loading={templatesLoading}
          onPick={(id) => { setImportOpen(false); handleClone(id); }}
          onClose={() => setImportOpen(false)}
        />

        {/* Toast */}
        {toast && (
          <Box sx={{ position: "fixed", bottom: 28, left: "50%", transform: "translateX(-50%)",
            bgcolor: "var(--color-ink)", color: "#fff", fontSize: "13.5px", fontWeight: 600,
            p: "12px 20px", borderRadius: "12px", boxShadow: "0 8px 24px -10px rgba(20,24,40,.14)",
            display: "flex", alignItems: "center", gap: "10px", zIndex: 400,
          }}>
            {toast}
          </Box>
        )}

        {activeTab === 1 && (
          <TemplatesPane
            templates={templates}
            loading={templatesLoading}
            cloningId={cloningId}
            onClone={handleClone}
            onUnmark={(id) => setDeleteTemplate(templates.find((t) => t.id === id) || { id, title: "this template" })}
            tplSearch={tplSearch}
            setTplSearch={setTplSearch}
            tplCatFilter={tplCatFilter}
            setTplCatFilter={setTplCatFilter}
          />
        )}
      </Box>

      {/* Create Course Dialog */}
      <CourseDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onSave={load}
        tenantId={selectedTenantId} instructors={instructors} />
    </Box>
  );
}
